import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Bell, Check } from "lucide-react";

/**
 * Admin notification bell — sits in the DashboardLayout header. Polls the
 * unread-count endpoint every 30s, opens a panel with the last 20 items.
 */
export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);

  const fetchCount = async () => {
    try {
      const { data } = await api.get("/admin/notifications/unread-count");
      setUnread(data.unread || 0);
    } catch { /* silent */ }
  };

  const fetchItems = async () => {
    try {
      const { data } = await api.get("/admin/notifications?limit=20");
      setItems(data.items || []);
    } catch { /* silent */ }
  };

  useEffect(() => {
    fetchCount();
    const t = setInterval(fetchCount, 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (open) fetchItems();
  }, [open]);

  const markRead = async (id) => {
    try {
      await api.post(`/admin/notifications/${id}/read`);
      setItems((xs) => xs.map((x) => x.id === id ? { ...x, read_at: new Date().toISOString() } : x));
      fetchCount();
    } catch { /* silent */ }
  };

  const markAll = async () => {
    try {
      await api.post("/admin/notifications/mark-all-read");
      toast.success("All notifications marked read");
      fetchItems(); fetchCount();
    } catch { /* silent */ }
  };

  return (
    <div className="relative" data-testid="notification-bell-wrap">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="relative w-9 h-9 flex items-center justify-center hover:bg-[hsl(var(--kb-surface-alt))] rounded-full"
        aria-label="Notifications"
        data-testid="notification-bell-button"
      >
        <Bell size={18} />
        {unread > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 bg-[hsl(var(--kb-primary))] text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center font-medium"
            data-testid="notification-unread-badge"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} aria-hidden="true" />
          <div
            className="absolute right-0 mt-2 w-80 max-h-96 overflow-auto bg-white border border-[hsl(var(--kb-border))] shadow-xl z-50"
            data-testid="notification-panel"
          >
            <div className="px-4 py-2 border-b border-[hsl(var(--kb-border))] flex items-center justify-between">
              <div className="font-medium text-sm">Notifications</div>
              {items.some((n) => !n.read_at) && (
                <button
                  type="button"
                  onClick={markAll}
                  className="text-xs underline text-[hsl(var(--kb-muted))]"
                  data-testid="notification-mark-all"
                >
                  Mark all read
                </button>
              )}
            </div>
            {items.length === 0 ? (
              <div className="p-6 text-center text-sm text-[hsl(var(--kb-muted))]">
                No notifications yet.
              </div>
            ) : (
              <ul>
                {items.map((n) => (
                  <li
                    key={n.id}
                    className={`px-4 py-3 border-b border-[hsl(var(--kb-border))] last:border-0 ${!n.read_at ? "bg-[hsl(var(--kb-surface-alt))]" : ""}`}
                    data-testid={`notification-item-${n.id}`}
                  >
                    <div className="text-sm font-medium">{n.title}</div>
                    <div className="text-xs text-[hsl(var(--kb-muted))] mt-1">{n.body}</div>
                    <div className="flex items-center justify-between mt-2 gap-2">
                      {n.link ? (
                        <Link
                          to={n.link}
                          onClick={() => { markRead(n.id); setOpen(false); }}
                          className="text-xs underline text-[hsl(var(--kb-primary))]"
                          data-testid={`notification-link-${n.id}`}
                        >
                          Open →
                        </Link>
                      ) : <span />}
                      {!n.read_at && (
                        <button
                          type="button"
                          onClick={() => markRead(n.id)}
                          className="text-[10px] text-[hsl(var(--kb-muted))] hover:text-[hsl(var(--kb-primary))] inline-flex items-center gap-1"
                          aria-label="Mark as read"
                        >
                          <Check size={10} /> Mark read
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
}
